import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Film, Link2, Send, Upload } from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { FileUpload } from '@/components/ui/FileUpload';
import { Input } from '@/components/ui/Input';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';
import type {
  RecordedClass,
  RecordingLibrary,
  UpdateRecordingPayload,
  VideoSource,
} from '@/types/recording';
import { recordingsApi, sendVideoToStorage } from '../api/recordings.api';
import { readLinkDuration } from '../api/videoDuration.api';
import type { TaughtClass } from '../types/teacher';

import { RecordedClassesHeader } from './recordings/RecordedClassesHeader';
import { RecordedClassesStats } from './recordings/RecordedClassesStats';
import {
  RecordedClassesFilterBar,
  type RecordingTabType,
} from './recordings/RecordedClassesFilterBar';
import { RecordedClassesTable } from './recordings/RecordedClassesTable';
import { RecordedClassesPagination } from './recordings/RecordedClassesPagination';
import { readFileDuration, readMediaDuration } from './recordings/videoDuration';

interface TeacherVideosSectionProps {
  currentClass: TaughtClass;
  allocatedClasses?: TaughtClass[];
  selectedClassId?: string;
  onSelectClass?: (id: string) => void;
}

const EMPTY_FORM = { title: '', topic: '', videoUrl: '', description: '' };
/** Pause after the last keystroke in the link before asking its host for a length. */
const LINK_LOOKUP_DELAY_MS = 600;

/** Where a lesson's length is read from: a key naming the video, and how to read it. */
interface LengthTarget {
  key: string;
  read: () => Promise<number | null>;
}
const VIDEO_ACCEPT = 'video/mp4,video/webm,video/quicktime';
const SOURCE_TABS: { value: VideoSource; label: string; icon: typeof Link2 }[] = [
  { value: 'LINK', label: 'Paste a link', icon: Link2 },
  { value: 'UPLOAD', label: 'Upload a file', icon: Upload },
];

/**
 * The library's view, kept in its address.
 *
 * Opening a lesson unmounts this screen, so anything held only in state is gone
 * by the time the teacher comes back. In the address it survives the lesson
 * page, the browser's Back button and a reload, and a teacher can bookmark the
 * view they use. Every value is read defensively — the address is typed by
 * anybody — and a bad one simply falls back to the default.
 */
const VIEW_PARAM = {
  classId: 'class',
  tab: 'view',
  search: 'q',
  topic: 'chapter',
  onDate: 'date',
  page: 'page',
  pageSize: 'rows',
} as const;
const RECORDING_TABS: readonly RecordingTabType[] = ['all', 'subject', 'date'];
/** The upload cap assumed only when the server's own figure could not be loaded. */
const FALLBACK_MAX_VIDEO_MB = 500;

/** The page sizes the pagination offers; anything else in the address is ignored. */
const PAGE_SIZES = [6, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;

function tabFrom(value: string | null): RecordingTabType {
  return RECORDING_TABS.find((tab) => tab === value) ?? 'all';
}

function dayFrom(value: string | null): string {
  return value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : '';
}

function positiveIntFrom(value: string | null): number | null {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}



export function TeacherVideosSection({
  currentClass,
  allocatedClasses = [],
  selectedClassId,
  onSelectClass,
}: TeacherVideosSectionProps) {
  // Filters & State, seeded from the address so a return visit lands where it left.
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<RecordingTabType>(() =>
    tabFrom(searchParams.get(VIEW_PARAM.tab)),
  );
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get(VIEW_PARAM.search) ?? '');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [onDate, setOnDate] = useState(() => dayFrom(searchParams.get(VIEW_PARAM.onDate)));
  const [topic, setTopic] = useState(() => searchParams.get(VIEW_PARAM.topic) ?? '');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(
    () => positiveIntFrom(searchParams.get(VIEW_PARAM.page)) ?? 1,
  );
  const [pageSize, setPageSize] = useState(() => {
    const rows = positiveIntFrom(searchParams.get(VIEW_PARAM.pageSize));
    return rows !== null && PAGE_SIZES.includes(rows) ? rows : DEFAULT_PAGE_SIZE;
  });

  // Form State
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [removing, setRemoving] = useState<RecordedClass | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  /**
   * The subject the lesson is filed under. Asked for rather than taken from the
   * Active Class silently: a teacher of three subjects in one class posted a
   * Physics lesson as English because the picker at the top still said English.
   */
  const [subjectUuid, setSubjectUuid] = useState(currentClass.subjectUuid);
  const [videoSource, setVideoSource] = useState<VideoSource>('LINK');
  const [file, setFile] = useState<File | null>(null);
  const [uploadPercent, setUploadPercent] = useState<number | null>(null);
  const uploadAbort = useRef<AbortController | null>(null);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  /** Whether an edit keeps the file the lesson already has. Removing it asks for a new one. */
  const [keepsSavedFile, setKeepsSavedFile] = useState(true);
  /** The length lookup started for the video now in the form, so saving reuses it. */
  const lengthLookup = useRef<{ key: string; seconds: Promise<number | null> } | null>(null);
  /** The lesson being corrected, when the form is open to edit rather than to post. */
  const [editing, setEditing] = useState<RecordedClass | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Clear filters when switching classes so previous class filters/dates do not persist.
  // Not when the first real class arrives: on a return visit the screen mounts on
  // the empty placeholder while the allotments load, and clearing then would wipe
  // the filters just restored from the address.
  const prevClassIdRef = useRef(currentClass.id);
  useEffect(() => {
    const previous = prevClassIdRef.current;
    prevClassIdRef.current = currentClass.id;
    if (previous && previous !== currentClass.id) {
      setOnDate('');
      setTopic('');
      setSearchQuery('');
      setCurrentPage(1);
    }
  }, [currentClass.id]);

  // Mirror the view into the address. `replace`, so filtering does not stack up
  // history entries the Back button would have to walk through. Other parameters
  // in the address are left alone; defaults are dropped to keep it short.
  useEffect(() => {
    // The placeholder class has no id; writing then would drop the one the
    // address was opened with before the allotments have loaded.
    if (!currentClass.id) return;

    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        const values: Record<string, string> = {
          [VIEW_PARAM.classId]: currentClass.id,
          [VIEW_PARAM.tab]: activeTab === 'all' ? '' : activeTab,
          [VIEW_PARAM.search]: searchQuery.trim(),
          [VIEW_PARAM.topic]: topic,
          [VIEW_PARAM.onDate]: onDate,
          [VIEW_PARAM.page]: currentPage > 1 ? String(currentPage) : '',
          [VIEW_PARAM.pageSize]: pageSize === DEFAULT_PAGE_SIZE ? '' : String(pageSize),
        };
        for (const [key, value] of Object.entries(values)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );
  }, [currentClass.id, activeTab, searchQuery, topic, onDate, currentPage, pageSize, setSearchParams]);

  function handleSelectClass(id: string) {
    setOnDate('');
    setTopic('');
    setSearchQuery('');
    setCurrentPage(1);
    onSelectClass?.(id);
  }

  const sectionUuid = currentClass.classSectionUuid;

  const { data, isLoading, error, refetch } = useApiQuery<RecordingLibrary | null>(
    () =>
      sectionUuid
        ? recordingsApi.list(sectionUuid, { onDate: onDate || undefined, topic: topic || undefined })
        : Promise.resolve(null),
    [sectionUuid, onDate, topic],
  );

  /** What this teacher takes in the active section — the subjects a lesson here can be filed under. */
  const subjectOptions = useMemo(() => {
    const bySubject = new Map([[currentClass.subjectUuid, currentClass.subject]]);
    for (const taught of allocatedClasses) {
      if (taught.classSectionUuid === currentClass.classSectionUuid && taught.subjectUuid) {
        bySubject.set(taught.subjectUuid, taught.subject);
      }
    }
    return [...bySubject].map(([value, label]) => ({ value, label: capitalizeWords(label) }));
  }, [allocatedClasses, currentClass]);

  const subjectName =
    subjectOptions.find((option) => option.value === subjectUuid)?.label ??
    capitalizeWords(currentClass.subject);

  // A new Active Class starts the form on its subject.
  useEffect(() => {
    setSubjectUuid(currentClass.subjectUuid);
  }, [currentClass.subjectUuid, currentClass.classSectionUuid]);

  function resetForm() {
    setForm(EMPTY_FORM);
    setSubjectUuid(currentClass.subjectUuid);
    setFile(null);
    setVideoSource('LINK');
    setUploadPercent(null);
    setErrors({});
    setEditing(null);
    setKeepsSavedFile(true);
    lengthLookup.current = null;
  }

  /** Opens the one form, filled with the lesson as it stands. */
  function startEditing(recording: RecordedClass) {
    setForm({
      title: recording.title,
      topic: recording.topic ?? '',
      videoUrl: recording.videoUrl ?? '',
      description: recording.description ?? '',
    });
    setSubjectUuid(recording.subject.uuid);
    setVideoSource(recording.videoSource);
    setFile(null);
    setErrors({});
    setKeepsSavedFile(true);
    lengthLookup.current = null;
    setEditing(recording);
    setIsFormOpen(true);
  }

  /**
   * The video the lesson's length is read from — the teacher is never asked
   * for it. Null when there is nothing to read: no video yet, or an edit that
   * keeps both its video and the length saved with it. A lesson saved before
   * lengths were read has none, so its own video is read and opening Edit and
   * saving is enough to fill it in.
   */
  function lengthTarget(): LengthTarget | null {
    if (videoSource === 'UPLOAD') {
      if (file) {
        const chosen = file;
        return {
          key: `file:${chosen.name}:${chosen.size}:${chosen.lastModified}`,
          read: () => readFileDuration(chosen),
        };
      }

      const saved = editing?.videoSource === 'UPLOAD' && keepsSavedFile ? editing : null;
      if (!saved || saved.durationSeconds !== null || !saved.playbackUrl) return null;
      const playbackUrl = saved.playbackUrl;
      return { key: `saved:${saved.uuid}`, read: () => readMediaDuration(playbackUrl) };
    }

    const url = form.videoUrl.trim();
    if (!url) return null;
    if (editing?.videoUrl === url && editing.durationSeconds !== null) return null;
    return { key: `link:${url}`, read: () => readLinkDuration(url) };
  }

  /** Starts the lookup for this video once; asking again returns the same answer. */
  function lookupLength(target: LengthTarget) {
    if (lengthLookup.current?.key !== target.key) {
      lengthLookup.current = { key: target.key, seconds: target.read() };
    }
    return lengthLookup.current.seconds;
  }

  // Read ahead, so saving rarely has to wait: a file or a stored upload at once,
  // a link once the teacher stops typing it.
  const target = lengthTarget();
  const targetKey = target?.key ?? null;
  useEffect(() => {
    if (!target) return;

    const timer = window.setTimeout(
      () => void lookupLength(target),
      target.key.startsWith('link:') ? LINK_LOOKUP_DELAY_MS : 0,
    );
    return () => window.clearTimeout(timer);
  }, [targetKey]);

  // The form sits above the table, usually off-screen from the row that opened it.
  useEffect(() => {
    if (editing) formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [editing]);

  /** Sends a chosen file straight to storage and returns the key the server issued. */
  async function uploadVideo(selected: File) {
    const target = await recordingsApi.createUploadTarget({
      classSectionUuid: sectionUuid,
      subjectUuid,
      filename: selected.name,
      mimeType: selected.type,
      sizeBytes: selected.size,
    });

    uploadAbort.current = new AbortController();
    setUploadPercent(0);

    await sendVideoToStorage(target, selected, {
      onProgress: setUploadPercent,
      signal: uploadAbort.current.signal,
    });

    return target.videoKey;
  }

  /**
   * The video part of an edit, or nothing when the video stays as it is: the
   * same link left alone, or the upload tab with no new file chosen.
   */
  async function replacementVideo(
    recording: RecordedClass,
  ): Promise<UpdateRecordingPayload['video']> {
    if (videoSource === 'UPLOAD') {
      if (!file) return undefined;
      return { videoSource: 'UPLOAD', videoKey: await uploadVideo(file), videoName: file.name };
    }

    const videoUrl = form.videoUrl.trim();
    if (recording.videoSource === 'LINK' && videoUrl === recording.videoUrl) return undefined;
    return { videoSource: 'LINK', videoUrl };
  }

  async function handlePost(event: FormEvent) {
    event.preventDefault();
    if (isFileMissing) return;

    setErrors({});
    setIsSaving(true);

    try {
      // Undefined leaves the saved length alone; null is a video that could not
      // be read, saved without a length rather than keeping the old video's.
      const durationSeconds = target ? await lookupLength(target) : undefined;

      const lesson = {
        classSectionUuid: sectionUuid,
        subjectUuid,
        title: form.title.trim(),
        ...(form.topic.trim() && { topic: form.topic.trim() }),
        ...(form.description.trim() && { description: form.description.trim() }),
        ...(typeof durationSeconds === 'number' && { durationSeconds }),
      };

      if (editing) {
        const video = await replacementVideo(editing);
        const updated = await recordingsApi.update(editing.uuid, {
          ...(subjectUuid !== editing.subject.uuid && { subjectUuid }),
          title: lesson.title,
          // Sent even when emptied, as `null`: leaving the key out would keep the
          // old text, and clearing a field is a correction like any other.
          topic: form.topic.trim() || null,
          description: form.description.trim() || null,
          ...(durationSeconds !== undefined && { durationSeconds }),
          ...(video && { video }),
        });

        toast.success(`“${updated.title}” is updated`, `${updated.displayName} · ${updated.subject.name}`);
        resetForm();
        setIsFormOpen(false);
        refetch();
        return;
      }

      let created;
      if (videoSource === 'UPLOAD' && file) {
        created = await recordingsApi.create({
          ...lesson,
          videoSource: 'UPLOAD',
          videoKey: await uploadVideo(file),
          videoName: file.name,
        });
      } else {
        created = await recordingsApi.create({
          ...lesson,
          videoSource: 'LINK',
          videoUrl: form.videoUrl.trim(),
        });
      }

      toast.success(
        `“${created.title}” is posted`,
        `${created.displayName} · ${created.subject.name}`,
      );
      resetForm();
      setIsFormOpen(false);
      refetch();
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        toast.info('Upload cancelled', editing ? 'Nothing was changed.' : 'Nothing was posted.');
        return;
      }

      if (caught instanceof ApiRequestError && caught.fields && caught.fields.length > 0) {
        const fieldErrors: Record<string, string> = {};
        for (const raw of caught.fields) {
          // An edit nests the video under `video.`; the form's fields are flat.
          const f = { ...raw, field: raw.field.replace(/^video\./, '') };
          if (f.field === 'videoKey' || f.field === 'videoName' || f.field === 'file') {
            fieldErrors.file = f.message;
          } else if (!fieldErrors[f.field]) {
            fieldErrors[f.field] = f.message;
          }
        }
        setErrors(fieldErrors);
        return;
      }

      toast.error(
        editing ? 'Could not update this lesson' : 'Could not post this lesson',
        caught instanceof ApiRequestError || caught instanceof Error
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      uploadAbort.current = null;
      setUploadPercent(null);
      setIsSaving(false);
    }
  }

  async function confirmRemove() {
    if (!removing) return;
    setIsRemoving(true);

    try {
      await recordingsApi.remove(removing.uuid);
      toast.success(`“${removing.title}” is suspended`, 'The recording is kept and can be restored.');
      setRemoving(null);
      refetch();
    } catch (caught) {
      toast.error(
        'Could not take this lesson down',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsRemoving(false);
    }
  }

  /**
   * The upload tab needs a file — except when editing a lesson that is already
   * an upload, where no file means "keep the one it has".
   */
  const isFileMissing =
    videoSource === 'UPLOAD' && !file && !(editing?.videoSource === 'UPLOAD' && keepsSavedFile);

  /** The file the upload slot shows: one just chosen, or the one an edit is keeping. */
  const shownFile =
    file ??
    (editing?.videoSource === 'UPLOAD' && keepsSavedFile
      ? { name: editing.videoName ?? 'Uploaded recording', size: editing.videoSize }
      : null);

  // Dynamic recordings from API
  const recordings = data?.recordings ?? [];
  const isFiltered = Boolean(onDate || topic || searchQuery);

  // Client-side search & tab filtering
  const filteredRecordings = useMemo(() => {
    let list = [...recordings];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          (r.topic && r.topic.toLowerCase().includes(q)) ||
          (r.subject?.name && r.subject.name.toLowerCase().includes(q)),
      );
    }

    // Newest first everywhere; "By Subject" keeps that order inside each subject,
    // so the table's group headings each sit over one contiguous run.
    list.sort((a, b) => new Date(b.postedAt).getTime() - new Date(a.postedAt).getTime());
    if (activeTab === 'subject') {
      list.sort((a, b) => (a.subject?.name || '').localeCompare(b.subject?.name || ''));
    }

    return list;
  }, [recordings, searchQuery, activeTab]);

  // Paginated recordings
  const paginatedRecordings = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRecordings.slice(start, start + pageSize);
  }, [filteredRecordings, currentPage, pageSize]);

  return (
    <div className="flex flex-col gap-6">
      {/* --- 1. Header Card --- */}
      <RecordedClassesHeader
        currentClass={currentClass}
        allocatedClasses={allocatedClasses}
        selectedClassId={selectedClassId}
        onSelectClass={onSelectClass ? handleSelectClass : undefined}
        isFormOpen={isFormOpen}
        onToggleForm={() => {
          // Closing mid-edit drops the edit, so the button next opens a blank post.
          if (isFormOpen && editing) resetForm();
          setIsFormOpen((prev) => !prev);
        }}
      />

      {/* --- 2. Summary Metrics Cards (100% dynamic from API data) --- */}
      <RecordedClassesStats
        // Search runs in the browser and leaves the summary alone; date and
        // topic go to the server, which works the summary out after them.
        isFiltered={Boolean(onDate || topic)}
        // Counted from the same list the server summed, so the two agree.
        lessonsWithoutLength={recordings.filter((r) => r.durationSeconds === null).length}
        summary={
          data?.summary || {
            lessons: recordings.length,
            topics: new Set(recordings.map((r) => r.topic).filter(Boolean)).size,
            totalMinutes: Math.round(
              recordings.reduce((acc, r) => acc + (r.durationSeconds ?? 0), 0) / 60,
            ),
            totalViews: recordings.reduce((acc, r) => acc + (r.viewCount || 0), 0),
          }
        }
      />

      {/* --- 3. Collapsible Post Form --- */}
      {isFormOpen && (
        <form
          ref={formRef}
          onSubmit={handlePost}
          noValidate
          aria-label={editing ? 'Edit recorded lesson' : 'Post a recorded lesson'}
          className="card flex flex-col gap-4 p-5 rounded-[20px] border border-line bg-surface shadow-md">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line pb-3">
            <h3 className="text-base sm:text-lg font-bold text-ink tracking-wide [word-spacing:0.2em]">
              {editing ? 'Edit recorded lesson' : 'Post a recorded lesson'}
            </h3>
            <span className="pill bg-indigo-500/15 text-indigo-400 font-bold border border-indigo-500/20 text-sm">
              {currentClass.className} · {subjectName}
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {/* Always shown, even with one subject: hidden, teachers could not see
              or confirm what the lesson would be filed under. */}
            <Select
              label="Subject"
              labelClassName="text-sm font-semibold"
              hideSearch
              options={subjectOptions}
              value={subjectUuid}
              hint={`Which of your subjects in ${currentClass.className} this lesson is for.`}
              onChange={(event) => setSubjectUuid(event.target.value)}
            />

            <Input
              label="Lesson title"
              labelClassName="text-sm font-semibold"
              className="text-[15px] sm:text-base placeholder:text-[15px] sm:placeholder:text-base"
              placeholder="e.g. Quadratic equations — solving by factorisation"
              required
              maxLength={150}
              error={errors.title}
              value={form.title}
              onChange={(event) => {
                setForm({ ...form, title: event.target.value });
                if (errors.title) setErrors((prev) => ({ ...prev, title: undefined }));
              }}
            />

            <Input
              label="Chapter or topic (optional)"
              labelClassName="text-sm font-semibold"
              className="text-[15px] sm:text-base placeholder:text-[15px] sm:placeholder:text-base"
              placeholder="e.g. Chapter 4"
              maxLength={120}
              hint="Used to group and filter the library."
              error={errors.topic}
              value={form.topic}
              onChange={(event) => {
                setForm({ ...form, topic: event.target.value });
                if (errors.topic) setErrors((prev) => ({ ...prev, topic: undefined }));
              }}
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-bold text-slate-700 text-ink">Video source</label>
            <div
              role="tablist"
              aria-label="Where the video comes from"
              className="inline-flex items-center p-1 rounded-[12px] border border-line bg-surface-2 gap-1.5 w-full sm:w-fit shadow-2xs"
            >
              {SOURCE_TABS.map((tab) => {
                const isActive = videoSource === tab.value;
                const isDisabled =
                  isSaving || (tab.value === 'UPLOAD' && data?.uploadsEnabled === false);

                return (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    disabled={isDisabled}
                    onClick={() => {
                      setVideoSource(tab.value);
                      setErrors((prev) => ({
                        ...prev,
                        videoUrl: undefined,
                        file: undefined,
                        videoKey: undefined,
                        videoName: undefined,
                      }));
                    }}
                    className={cn(
                      'flex flex-1 sm:flex-initial items-center justify-center gap-2 rounded-[9px] px-4 py-2 text-sm font-bold whitespace-nowrap transition-all cursor-pointer',
                      'focus:outline-none focus:ring-2 focus:ring-indigo-500/25',
                      'disabled:cursor-not-allowed disabled:opacity-50',
                      isActive
                        ? 'bg-surface text-indigo-600 dark:text-indigo-400 shadow-xs border border-line/80 font-extrabold'
                        : 'text-ink-3 hover:text-ink hover:bg-surface/60 font-semibold',
                    )}
                  >
                    <tab.icon
                      className={cn(
                        'h-4 w-4 shrink-0',
                        isActive ? 'text-indigo-600 dark:text-indigo-400' : 'text-ink-3',
                      )}
                      aria-hidden
                    />
                    <span className="whitespace-nowrap">{tab.label}</span>
                  </button>
                );
              })}
            </div>

            {data && !data.uploadsEnabled && (
              <Alert tone="info" title="Uploading is not switched on here">
                Recordings are stored by your institute's file service, which this install does not
                have. Paste a link to the recording instead.
              </Alert>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4">
            {videoSource === 'LINK' ? (
              <Input
                label="Link to the recording"
                labelClassName="text-sm font-semibold"
                className="text-[15px] sm:text-base placeholder:text-[15px] sm:placeholder:text-base"
                type="url"
                required
                maxLength={500}
                placeholder="https://…"
                hint="A YouTube, Drive, Vimeo or Zoom share link. It must start with https."
                error={errors.videoUrl}
                value={form.videoUrl}
                onChange={(event) => {
                  setForm({ ...form, videoUrl: event.target.value });
                  if (errors.videoUrl) setErrors((prev) => ({ ...prev, videoUrl: undefined }));
                }}
              />
            ) : !data && isLoading ? (
              // The size cap is the server's; shown once it has arrived rather
              // than guessed while it loads.
              <p className="text-sm font-medium text-ink-3">Checking how large a recording can be…</p>
            ) : (
              <FileUpload
                label="The recording"
                accept={VIDEO_ACCEPT}
                // If the list failed to load the upload still works: the server
                // enforces its own limit and refuses anything larger, so this
                // fallback only shapes the hint and the early client check.
                maxSizeMb={data?.maxVideoUploadMb ?? FALLBACK_MAX_VIDEO_MB}
                file={shownFile}
                onSelect={(selected) => {
                  setFile(selected);
                  // Taken off: an edit's stored file goes too, and a new one is needed.
                  if (!selected) setKeepsSavedFile(false);
                  if (errors.file || errors.videoKey || errors.videoName) {
                    setErrors((prev) => ({
                      ...prev,
                      file: undefined,
                      videoKey: undefined,
                      videoName: undefined,
                    }));
                  }
                }}
                disabled={isSaving}
                error={errors.file || errors.videoKey || errors.videoName}
                hint={
                  shownFile && !file
                    ? 'The recording this lesson has now. Remove it to upload a different one.'
                    : `MP4, WEBM or MOV up to ${data?.maxVideoUploadMb ?? FALLBACK_MAX_VIDEO_MB} MB. Only this class sees it.`
                }
              />
            )}
          </div>

          {uploadPercent !== null && (
            <ProgressBar
              label="Sending the recording"
              caption={`${uploadPercent}%`}
              value={uploadPercent}
            />
          )}

          <Textarea
            label="What the lesson covers (optional)"
            labelClassName="text-sm font-semibold"
            className="text-[15px] sm:text-base placeholder:text-[15px] sm:placeholder:text-base"
            rows={3}
            maxLength={2000}
            placeholder="Which examples were worked, what to revise before watching…"
            error={errors.description}
            value={form.description}
            onChange={(event) => {
              setForm({ ...form, description: event.target.value });
              if (errors.description) {
                setErrors((prev) => ({ ...prev, description: undefined }));
              }
            }}
          />

          <div className="flex items-center justify-end gap-2.5 border-t border-line pt-3">
            <Button
              type="button"
              variant="secondary"
              size="md"
              className="text-sm sm:text-[14px] text-slate-700 font-bold px-4"
              onClick={() => {
                uploadAbort.current?.abort();
                resetForm();
                setIsFormOpen(false);
              }}
            >
              {uploadPercent === null ? 'Cancel' : 'Cancel upload'}
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="text-sm sm:text-[14px] font-bold px-4 bg-indigo-600 hover:bg-indigo-700 text-white"
              isLoading={isSaving}
              disabled={isFileMissing}
              leftIcon={<Send className="h-4 w-4" />}
            >
              {uploadPercent !== null
                ? `Sending… ${uploadPercent}%`
                : editing
                  ? 'Save changes'
                  : 'Post lesson'}
            </Button>
          </div>
        </form>
      )}

      {/* --- 4. Navigation Tabs & Search/Filter Bar --- */}
      <RecordedClassesFilterBar
        activeTab={activeTab}
        onTabChange={(tab) => {
          setActiveTab(tab);
          // The order changes under the reader, so page 3 of one is not page 3 of the other.
          setCurrentPage(1);
        }}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        showAdvancedFilters={showAdvancedFilters}
        onToggleAdvancedFilters={() => setShowAdvancedFilters((v) => !v)}
        topic={topic}
        onTopicChange={setTopic}
        topicOptions={data?.topicOptions ?? []}
        onDate={onDate}
        onDateChange={setOnDate}
        onClearFilters={() => {
          setTopic('');
          setOnDate('');
          setSearchQuery('');
          setCurrentPage(1);
        }}
        isFiltered={isFiltered}
      />

      {/* --- 5. Recordings Table Content --- */}
      {isLoading && (
        <div className="card p-5 rounded-[20px]">
          <SkeletonLines count={6} />
        </div>
      )}

      {!isLoading && error && (
        <div className="card flex flex-col items-start gap-3 p-5 rounded-[20px]">
          <Alert tone="critical" title="Could not load these lessons">
            {error.message}
          </Alert>
          <Button variant="secondary" size="sm" onClick={refetch}>
            Try again
          </Button>
        </div>
      )}

      {!isLoading && !error && filteredRecordings.length === 0 && (
        <div className="card rounded-[20px]">
          <EmptyState
            icon={<Film className="h-5 w-5" />}
            title={isFiltered ? 'Nothing matches those filters' : 'No lessons posted yet'}
            description={
              isFiltered
                ? 'Clear the filters or search query to see the recordings library.'
                : 'Paste a link to a recorded lesson, or upload the file itself, and it appears on the children’s panel straight away.'
            }
          />
        </div>
      )}

      {!isLoading && !error && filteredRecordings.length > 0 && (
        <>
          <RecordedClassesTable
            recordings={paginatedRecordings}
            onEdit={startEditing}
            onRemove={(item) => setRemoving(item)}
            groupBy={activeTab === 'all' ? undefined : activeTab}
          />

          {/* --- 6. Footer & Pagination --- */}
          <RecordedClassesPagination
            totalItems={filteredRecordings.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
        </>
      )}

      {/* Confirm Suspend Dialog */}
      <ConfirmDialog
        isOpen={Boolean(removing)}
        tone="danger"
        title={`Suspend “${removing?.title ?? 'this lesson'}”?`}
        description="Nothing is deleted. It stops appearing for the children, and the record of who watched it is kept."
        confirmLabel="Suspend recording"
        isLoading={isRemoving}
        onConfirm={() => void confirmRemove()}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}
